import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

import { useSearchParams } from '@remix-run/react';

import {
  getCarouselSteps,
  ICarouselTimerData,
} from 'growth/components/welcomeToHub/WelcomeToHubCarousel';
import {
  IWelcomeToHubLoadingPageData,
  timedMessages,
  WelcomeToHubVariant,
} from 'growth/components/welcomeToHub/WelcomeToHubLoadingPage';

export interface IWelcomeToHubContext {
  wthVariant: WelcomeToHubVariant;
  carouselTimerData: ICarouselTimerData;
  welcomeToHubLoadingPageData: IWelcomeToHubLoadingPageData;
}

/** Default loading context values */
export const defaultWelcomeToHubContext: IWelcomeToHubContext = {
  wthVariant: null,
  carouselTimerData: {
    stepPaused: false,
    setStepPaused: () => {},
    stepIndex: 0,
    setStepIndex: () => {},
  },
  welcomeToHubLoadingPageData: {
    timedMessageIndex: 0,
  },
};

export const WelcomeToHubContext = createContext<IWelcomeToHubContext>(defaultWelcomeToHubContext);

/** Hook for getting loading page data */
export const useWelcomeToHubContext = () => useContext(WelcomeToHubContext);

/**
 * Context for loading page data
 *
 * storybook-check-ignore
 */
export const WelcomeToHubProvider: React.FC<
  PropsWithChildren<{
    showCarousel?: boolean; // for testing
    contextOverride?: Partial<IWelcomeToHubContext>;
  }>
> = ({ showCarousel, children }) => {
  const [queryParams] = useSearchParams();

  let wthVariant: WelcomeToHubVariant = null;
  if (queryParams.get('show_offer_refresh')) {
    // Eligibility is determined in Iterable before user enters the Hub
    wthVariant = 'offer_refresh_from_email';
  } else if (queryParams.get('show_loading_carousel') === 'true' || (showCarousel ?? false)) {
    // Eligibility is determined in Reception before user enters the Hub
    wthVariant = 'welcome_to_the_hub_v3';
  }

  const steps = getCarouselSteps(wthVariant);

  const [stepPaused, setStepPaused] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [timedMessageIndex, setTimedMessageIndex] = useState(0);

  useEffect(() => {
    const timedMessageTimer = setTimeout(() => {
      setTimedMessageIndex((value) => {
        if (value < timedMessages.length - 1) {
          value++;
        }
        return value;
      });
    }, 15000);

    return () => clearTimeout(timedMessageTimer);
  }, [timedMessageIndex]);

  useEffect(() => {
    const stepTimer = setTimeout(() => {
      // if user has paused, don't update state at all
      // meaning show the same step
      if (!wthVariant || stepPaused) {
        return;
      }

      setStepIndex((value) => {
        if (value < steps.length - 1) {
          value++;
        } else {
          value = 0;
        }
        return value;
      });
    }, steps[stepIndex].duration);

    return () => clearTimeout(stepTimer);
  }, [wthVariant, stepPaused, stepIndex, steps]);

  const carouselTimerData = {
    stepPaused,
    setStepPaused,
    stepIndex,
    setStepIndex,
  };

  const welcomeToHubLoadingPageData = {
    timedMessageIndex,
  };

  return (
    <WelcomeToHubContext.Provider
      value={{
        wthVariant: wthVariant,
        carouselTimerData: carouselTimerData,
        welcomeToHubLoadingPageData,
      }}
    >
      {children}
    </WelcomeToHubContext.Provider>
  );
};
